import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import {
  Users,
  Globe2,
  Upload,
  Repeat2,
  Sparkles,
  Loader2,
  Plus,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  pushAudienceToProvider,
  isReady,
  type CustomAudience,
  type RetargetingAudience,
  type LookalikeAudience,
} from "@/lib/audience-data";

export const Route = createFileRoute("/_app/audiences")({
  component: AudiencesPage,
  head: () => ({
    meta: [
      { title: "Audience Targeting · AdKavi" },
      {
        name: "description",
        content:
          "Build custom, retargeting, and lookalike audiences for Meta, TikTok, and Google ad campaigns.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type Campaign = { id: string; name: string };

function StatusBadge({ status }: { status: string }) {
  const { t } = useI18n();
  const ready = isReady(status as never);
  return (
    <Badge variant={ready ? "default" : "secondary"} className="gap-1">
      {ready ? (
        <CheckCircle2 className="h-3 w-3" />
      ) : (
        <Clock className="h-3 w-3" />
      )}
      {ready ? t("audiences.status.ready") : t("audiences.status.pending")}
    </Badge>
  );
}

function AudiencesPage() {
  const { t } = useI18n();
  const { user } = useAuth();

  const [customs, setCustoms] = useState<CustomAudience[]>([]);
  const [retargets, setRetargets] = useState<RetargetingAudience[]>([]);
  const [lookalikes, setLookalikes] = useState<LookalikeAudience[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  // Custom audience form
  const [caName, setCaName] = useState("");
  const [caUrl, setCaUrl] = useState("");
  const [caFileCount, setCaFileCount] = useState(0);
  const [caFileName, setCaFileName] = useState<string | null>(null);
  const [caBusy, setCaBusy] = useState(false);

  // Retargeting form
  const [rtName, setRtName] = useState("");
  const [rtCampaign, setRtCampaign] = useState<string>("");
  const [rtWindow, setRtWindow] = useState("30");
  const [rtBusy, setRtBusy] = useState(false);

  // Lookalike form
  const [laName, setLaName] = useState("");
  const [laSource, setLaSource] = useState<string>("");
  const [laPct, setLaPct] = useState<number>(1);
  const [laCountry, setLaCountry] = useState("US");
  const [laBusy, setLaBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadAll() {
    setLoading(true);
    const [c, r, l, camp] = await Promise.all([
      supabase
        .from("custom_audiences")
        .select("*")
        .order("created_at", { ascending: false }),
      supabase
        .from("retargeting_audiences")
        .select("*")
        .order("created_at", { ascending: false }),
      supabase
        .from("lookalike_audiences")
        .select("*")
        .order("created_at", { ascending: false }),
      supabase.from("campaigns").select("id,name").order("created_at", { ascending: false }),
    ]);
    if (c.data) setCustoms(c.data as CustomAudience[]);
    if (r.data) setRetargets(r.data as RetargetingAudience[]);
    if (l.data) setLookalikes(l.data as LookalikeAudience[]);
    if (camp.data) setCampaigns(camp.data as Campaign[]);
    setLoading(false);
  }

  function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? "");
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
      // Subtract 1 if it looks like a header
      const count = lines.length > 0 && /[a-zA-Z]/.test(lines[0]) && lines[0].includes("@") === false
        ? Math.max(0, lines.length - 1)
        : lines.length;
      setCaFileCount(count);
      setCaFileName(file.name);
    };
    reader.readAsText(file);
  }

  async function createCustom(sourceType: "website" | "customer_list") {
    if (!user) return;
    if (!caName.trim()) {
      toast.error(t("audiences.error.nameRequired"));
      return;
    }
    if (sourceType === "website" && !caUrl.trim()) {
      toast.error(t("audiences.error.urlRequired"));
      return;
    }
    if (sourceType === "customer_list" && caFileCount === 0) {
      toast.error(t("audiences.error.fileRequired"));
      return;
    }
    setCaBusy(true);
    const { data, error } = await supabase
      .from("custom_audiences")
      .insert({
        user_id: user.id,
        name: caName.trim(),
        source_type: sourceType,
        source_url: sourceType === "website" ? caUrl.trim() : null,
        contact_count: sourceType === "customer_list" ? caFileCount : 0,
      })
      .select()
      .single();
    setCaBusy(false);
    if (error || !data) {
      toast.error(t("audiences.error.createFailed"));
      return;
    }
    await pushAudienceToProvider("meta", data.id);
    setCustoms((prev) => [data as CustomAudience, ...prev]);
    setCaName("");
    setCaUrl("");
    setCaFileCount(0);
    setCaFileName(null);
    toast.success(t("audiences.toast.customCreated"));
  }

  async function createRetarget() {
    if (!user) return;
    if (!rtName.trim() || !rtCampaign) {
      toast.error(t("audiences.error.retargetFields"));
      return;
    }
    setRtBusy(true);
    const { data, error } = await supabase
      .from("retargeting_audiences")
      .insert({
        user_id: user.id,
        name: rtName.trim(),
        campaign_id: rtCampaign,
        condition: "saw_ad_no_convert",
        window_days: Number(rtWindow),
      })
      .select()
      .single();
    setRtBusy(false);
    if (error || !data) {
      toast.error(t("audiences.error.createFailed"));
      return;
    }
    await pushAudienceToProvider("meta", data.id);
    setRetargets((prev) => [data as RetargetingAudience, ...prev]);
    setRtName("");
    setRtCampaign("");
    toast.success(t("audiences.toast.retargetCreated"));
  }

  async function createLookalike() {
    if (!user) return;
    if (!laName.trim() || !laSource) {
      toast.error(t("audiences.error.lookalikeFields"));
      return;
    }
    setLaBusy(true);
    const { data, error } = await supabase
      .from("lookalike_audiences")
      .insert({
        user_id: user.id,
        name: laName.trim(),
        source_audience_id: laSource,
        similarity_pct: laPct,
        country: laCountry,
      })
      .select()
      .single();
    setLaBusy(false);
    if (error || !data) {
      toast.error(t("audiences.error.createFailed"));
      return;
    }
    await pushAudienceToProvider("meta", data.id);
    setLookalikes((prev) => [data as LookalikeAudience, ...prev]);
    setLaName("");
    setLaSource("");
    setLaPct(1);
    toast.success(t("audiences.toast.lookalikeCreated"));
  }

  const customById = useMemo(() => {
    const m = new Map<string, CustomAudience>();
    customs.forEach((c) => m.set(c.id, c));
    return m;
  }, [customs]);

  const campaignById = useMemo(() => {
    const m = new Map<string, string>();
    campaigns.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [campaigns]);

  return (
    <div className="container max-w-6xl py-8 space-y-6">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4" />
          <span>{t("audiences.eyebrow")}</span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <Users className="w-8 h-8 text-orange-400 shrink-0" />
          <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">{t("audiences.title")}</span>
        </h1>
        <p className="text-muted-foreground max-w-2xl">{t("audiences.subtitle")}</p>
      </header>

      <Tabs defaultValue="custom" className="space-y-6">
        <TabsList>
          <TabsTrigger value="custom" className="gap-2">
            <Globe2 className="h-4 w-4" /> {t("audiences.tabs.custom")}
          </TabsTrigger>
          <TabsTrigger value="retarget" className="gap-2">
            <Repeat2 className="h-4 w-4" /> {t("audiences.tabs.retarget")}
          </TabsTrigger>
          <TabsTrigger value="lookalike" className="gap-2">
            <Sparkles className="h-4 w-4" /> {t("audiences.tabs.lookalike")}
          </TabsTrigger>
        </TabsList>

        {/* CUSTOM */}
        <TabsContent value="custom" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("audiences.custom.title")}</CardTitle>
              <CardDescription>{t("audiences.custom.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>{t("audiences.field.name")}</Label>
                <Input
                  value={caName}
                  onChange={(e) => setCaName(e.target.value)}
                  placeholder={t("audiences.custom.namePlaceholder")}
                  maxLength={100}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3 p-4 rounded-lg border">
                  <div className="flex items-center gap-2 font-medium">
                    <Globe2 className="h-4 w-4" /> {t("audiences.custom.website")}
                  </div>
                  <Input
                    value={caUrl}
                    onChange={(e) => setCaUrl(e.target.value)}
                    placeholder="https://example.com"
                    maxLength={255}
                  />
                  <Button
                    onClick={() => createCustom("website")}
                    disabled={caBusy}
                    className="w-full"
                  >
                    {caBusy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        {t("audiences.custom.createFromWebsite")}
                      </>
                    )}
                  </Button>
                </div>

                <div className="space-y-3 p-4 rounded-lg border">
                  <div className="flex items-center gap-2 font-medium">
                    <Upload className="h-4 w-4" /> {t("audiences.custom.upload")}
                  </div>
                  <Input
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFile}
                  />
                  {caFileName && (
                    <p className="text-xs text-muted-foreground">
                      {caFileName} · {caFileCount} {t("audiences.custom.contacts")}
                    </p>
                  )}
                  <Button
                    onClick={() => createCustom("customer_list")}
                    disabled={caBusy}
                    variant="secondary"
                    className="w-full"
                  >
                    {caBusy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        {t("audiences.custom.createFromList")}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <AudienceList
            title={t("audiences.custom.listTitle")}
            empty={t("audiences.custom.empty")}
            loading={loading}
            items={customs.map((c) => ({
              id: c.id,
              title: c.name,
              subtitle:
                c.source_type === "website"
                  ? c.source_url || "—"
                  : `${c.contact_count} ${t("audiences.custom.contacts")}`,
              status: c.sync_status,
            }))}
          />
        </TabsContent>

        {/* RETARGETING */}
        <TabsContent value="retarget" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("audiences.retarget.title")}</CardTitle>
              <CardDescription>{t("audiences.retarget.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t("audiences.field.name")}</Label>
                <Input
                  value={rtName}
                  onChange={(e) => setRtName(e.target.value)}
                  placeholder={t("audiences.retarget.namePlaceholder")}
                  maxLength={100}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("audiences.retarget.campaign")}</Label>
                  <Select value={rtCampaign} onValueChange={setRtCampaign}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("audiences.retarget.pickCampaign")} />
                    </SelectTrigger>
                    <SelectContent>
                      {campaigns.length === 0 ? (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                          {t("audiences.retarget.noCampaigns")}
                        </div>
                      ) : (
                        campaigns.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("audiences.retarget.window")}</Label>
                  <Select value={rtWindow} onValueChange={setRtWindow}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 {t("audiences.retarget.days")}</SelectItem>
                      <SelectItem value="14">14 {t("audiences.retarget.days")}</SelectItem>
                      <SelectItem value="30">30 {t("audiences.retarget.days")}</SelectItem>
                      <SelectItem value="60">60 {t("audiences.retarget.days")}</SelectItem>
                      <SelectItem value="90">90 {t("audiences.retarget.days")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="rounded-md bg-muted/40 p-3 text-sm text-muted-foreground">
                {t("audiences.retarget.conditionLabel")}
              </div>
              <Button onClick={createRetarget} disabled={rtBusy}>
                {rtBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    {t("audiences.retarget.create")}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <AudienceList
            title={t("audiences.retarget.listTitle")}
            empty={t("audiences.retarget.empty")}
            loading={loading}
            items={retargets.map((r) => ({
              id: r.id,
              title: r.name,
              subtitle: `${campaignById.get(r.campaign_id) || "—"} · ${r.window_days} ${t("audiences.retarget.days")}`,
              status: r.sync_status,
            }))}
          />
        </TabsContent>

        {/* LOOKALIKE */}
        <TabsContent value="lookalike" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("audiences.lookalike.title")}</CardTitle>
              <CardDescription>{t("audiences.lookalike.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t("audiences.field.name")}</Label>
                <Input
                  value={laName}
                  onChange={(e) => setLaName(e.target.value)}
                  placeholder={t("audiences.lookalike.namePlaceholder")}
                  maxLength={100}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("audiences.lookalike.source")}</Label>
                  <Select value={laSource} onValueChange={setLaSource}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("audiences.lookalike.pickSource")} />
                    </SelectTrigger>
                    <SelectContent>
                      {customs.length === 0 ? (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                          {t("audiences.lookalike.noSources")}
                        </div>
                      ) : (
                        customs.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("audiences.lookalike.country")}</Label>
                  <Select value={laCountry} onValueChange={setLaCountry}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="US">United States</SelectItem>
                      <SelectItem value="MX">Mexico</SelectItem>
                      <SelectItem value="ES">Spain</SelectItem>
                      <SelectItem value="GB">United Kingdom</SelectItem>
                      <SelectItem value="CA">Canada</SelectItem>
                      <SelectItem value="AR">Argentina</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>
                  {t("audiences.lookalike.similarity")}: {laPct}%
                </Label>
                <Slider
                  value={[laPct]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(v) => setLaPct(v[0] ?? 1)}
                />
                <p className="text-xs text-muted-foreground">
                  {t("audiences.lookalike.similarityHint")}
                </p>
              </div>
              <Button onClick={createLookalike} disabled={laBusy}>
                {laBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    {t("audiences.lookalike.create")}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <AudienceList
            title={t("audiences.lookalike.listTitle")}
            empty={t("audiences.lookalike.empty")}
            loading={loading}
            items={lookalikes.map((l) => ({
              id: l.id,
              title: l.name,
              subtitle: `${customById.get(l.source_audience_id)?.name || "—"} · ${l.similarity_pct}% · ${l.country}`,
              status: l.sync_status,
            }))}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AudienceList({
  title,
  empty,
  loading,
  items,
}: {
  title: string;
  empty: string;
  loading: boolean;
  items: { id: string; title: string; subtitle: string; status: string }[];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{empty}</p>
        ) : (
          <ul className="divide-y">
            {items.map((i) => (
              <li key={i.id} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-medium truncate">{i.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{i.subtitle}</p>
                </div>
                <StatusBadge status={i.status} />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
